import { useEffect, useState } from 'react';
import axios from 'axios';
import { Settings2, Loader2, RefreshCw, Check, X, ExternalLink, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CointrackerSettings() {
  const [loading, setLoading] = useState(true);
  const [xeroConnected, setXeroConnected] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [importing, setImporting] = useState({ accounts: false, contacts: false });
  const [resetting, setResetting] = useState(false);
  
  const [settings, setSettings] = useState({
    default_crypto_account: '',
    default_fee_account: '',
    default_gains_account: '',
    default_losses_account: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchAccounts();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/cointracker/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data.settings || {});
      setXeroConnected(response.data.xero_connected || false);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/cointracker/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccounts(response.data.accounts || []);
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
    }
  };

  const handleConnectXero = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/cointracker/xero/connect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Redirect to Xero OAuth
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect Xero');
    }
  };

  const handleDisconnectXero = async () => {
    if (!confirm('Disconnect Xero? You will need to reconnect to sync journal entries.')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/cointracker/xero/disconnect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setXeroConnected(false);
      toast.success('Xero disconnected');
    } catch (error) {
      toast.error('Failed to disconnect Xero');
    }
  };

  const handleImportAccounts = async () => {
    setImporting(prev => ({ ...prev, accounts: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/cointracker/xero/import-accounts`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Imported ${response.data.count} accounts`);
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import accounts');
    } finally {
      setImporting(prev => ({ ...prev, accounts: false }));
    }
  };

  const handleImportContacts = async () => {
    setImporting(prev => ({ ...prev, contacts: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/cointracker/xero/import-contacts`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(response.data.contacts || []);
      toast.success(`Imported ${response.data.count} contacts`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import contacts');
    } finally {
      setImporting(prev => ({ ...prev, contacts: false }));
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/cointracker/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleResetData = async () => {
    setResetting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/cointracker/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const deleted = response.data.deleted;
      toast.success(`Reset complete: ${deleted.wallets} wallets, ${deleted.transactions} transactions, ${deleted.journal_entries} journal entries deleted`);
    } catch (error) {
      toast.error('Failed to reset data');
    } finally {
      setResetting(false);
    }
  };

  const [cleaningPhantom, setCleaningPhantom] = useState(false);
  const handleCleanupPhantomTxs = async () => {
    setCleaningPhantom(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/cointracker/cleanup-phantom-txs`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { deleted_transactions, deleted_journal_entries } = response.data;
      toast.success(`Cleanup done: ${deleted_transactions} phantom transactions and ${deleted_journal_entries} journal entries removed`);
    } catch (error) {
      toast.error('Failed to cleanup phantom transactions');
    } finally {
      setCleaningPhantom(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white">Settings</h2>
        <p className="text-sm text-zinc-400 mt-1">Configure integrations and default accounts</p>
      </div>

      {/* Xero Integration */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <div className="w-8 h-8 rounded bg-[#13B5EA] flex items-center justify-center">
              <span className="text-white font-bold text-sm">X</span>
            </div>
            Xero Integration
          </CardTitle>
          <CardDescription className="text-zinc-400">
            Connect to Xero to sync journal entries and import chart of accounts
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {xeroConnected ? (
            <>
              <div className="flex items-center gap-2 text-green-400">
                <Check className="w-5 h-5" />
                <span>Connected to Xero</span>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={handleImportAccounts}
                  disabled={importing.accounts}
                  className="border-zinc-700"
                >
                  {importing.accounts ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Import Accounts
                </Button>
                <Button
                  variant="outline"
                  onClick={handleImportContacts}
                  disabled={importing.contacts}
                  className="border-zinc-700"
                >
                  {importing.contacts ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Import Contacts
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDisconnectXero}
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                >
                  Disconnect
                </Button>
              </div>
            </>
          ) : (
            <Button
              onClick={handleConnectXero}
              className="bg-[#13B5EA] text-white hover:bg-[#0fa8db]"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Connect Xero
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Default GL Accounts */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white">Default General Ledger Accounts</CardTitle>
          <CardDescription className="text-zinc-400">
            Set default accounts for journal entry categorization. Rules override these settings.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm text-white font-medium">Crypto Assets</label>
              <p className="text-xs text-zinc-500">Account for cryptocurrency holdings and transactions</p>
              <Select
                value={settings.default_crypto_account}
                onValueChange={(value) => setSettings(prev => ({ ...prev, default_crypto_account: value }))}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Select Account..." />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700 max-h-60">
                  {accounts.map((account) => (
                    <SelectItem key={account.account_id} value={account.code} className="text-white">
                      #{account.code} {account.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-white font-medium">Transaction Fees</label>
              <p className="text-xs text-zinc-500">Account for blockchain transaction fees and gas costs</p>
              <Select
                value={settings.default_fee_account}
                onValueChange={(value) => setSettings(prev => ({ ...prev, default_fee_account: value }))}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Select Account..." />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700 max-h-60">
                  {accounts.map((account) => (
                    <SelectItem key={account.account_id} value={account.code} className="text-white">
                      #{account.code} {account.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-white font-medium">Realized Gains</label>
              <p className="text-xs text-zinc-500">Account for gains from completed transactions</p>
              <Select
                value={settings.default_gains_account}
                onValueChange={(value) => setSettings(prev => ({ ...prev, default_gains_account: value }))}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Select Account..." />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700 max-h-60">
                  {accounts.map((account) => (
                    <SelectItem key={account.account_id} value={account.code} className="text-white">
                      #{account.code} {account.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-white font-medium">Realized Losses</label>
              <p className="text-xs text-zinc-500">Account for losses from completed transactions</p>
              <Select
                value={settings.default_losses_account}
                onValueChange={(value) => setSettings(prev => ({ ...prev, default_losses_account: value }))}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Select Account..." />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700 max-h-60">
                  {accounts.map((account) => (
                    <SelectItem key={account.account_id} value={account.code} className="text-white">
                      #{account.code} {account.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="pt-4 border-t border-zinc-800">
            <Button
              onClick={handleSaveSettings}
              disabled={saving}
              className="bg-white text-black hover:bg-zinc-200"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Chart of Accounts Preview */}
      {accounts.length > 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white">Chart of Accounts</CardTitle>
            <CardDescription className="text-zinc-400">
              {accounts.length} accounts imported from Xero
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="max-h-64 overflow-y-auto space-y-1">
              {accounts.map((account) => (
                <div
                  key={account.account_id}
                  className="flex items-center justify-between p-2 hover:bg-zinc-800/50 rounded"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-zinc-500 font-mono w-16">#{account.code}</span>
                    <span className="text-white">{account.name}</span>
                  </div>
                  <span className="text-xs text-zinc-500 capitalize">{account.type}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Maintenance */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white">Maintenance</CardTitle>
          <CardDescription className="text-zinc-400">
            Safe cleanup actions that only remove noise data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-white font-medium">Cleanup phantom $0 Coinbase transactions</p>
              <p className="text-sm text-zinc-500">
                Removes zero-amount Coinbase entries that appear duplicated on every sync (dust / phantom events).
                Legit transactions with real amounts are kept.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={handleCleanupPhantomTxs}
              disabled={cleaningPhantom}
              className="border-zinc-700 whitespace-nowrap"
              data-testid="cointracker-cleanup-phantom-btn"
            >
              {cleaningPhantom ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Cleanup phantom TXs
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="bg-zinc-900/50 border-red-900/50">
        <CardHeader>
          <CardTitle className="text-red-400">Danger Zone</CardTitle>
          <CardDescription className="text-zinc-400">
            Irreversible actions. Use with caution.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white font-medium">Reset All Cointracker Data</p>
              <p className="text-sm text-zinc-500">Delete all wallets, transactions, and journal entries. This cannot be undone.</p>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                  disabled={resetting}
                >
                  {resetting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-2" />
                  )}
                  Reset Data
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-white">Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription className="text-zinc-400">
                    This will permanently delete all your Cointracker data including wallets, transactions, journal entries, and rules. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleResetData}
                    className="bg-red-600 text-white hover:bg-red-700"
                  >
                    Yes, delete everything
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
